package com.daytimegaming.ourdailyfinances.presentation.budget

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.Dashboard
import com.daytimegaming.ourdailyfinances.domain.usecase.BudgetUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.math.roundToLong

data class UpdateBudgetState(
    val name: String = "",
    val targetValue: String = "", // raw numeric string (digits only)
    val selectedCategories: List<String> = emptyList(),
    val availableDashboards: List<Dashboard> = emptyList(),
    val selectedDashboards: List<Dashboard> = emptyList(),
    val isLoading: Boolean = false,
    val error: String? = null,
    val success: Boolean = false,
    val isDeleted: Boolean = false,
)

class UpdateBudgetViewModel(
    private val budgetId: String,
    private val budgetUseCase: BudgetUseCase,
    private val dashboardUseCase: DashboardUseCase
) : ViewModel() {

    private val _state = MutableStateFlow(UpdateBudgetState())
    val state = _state.asStateFlow()

    private var isInitialized = false

    init {
        loadData()
    }

    private fun loadData() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            
            combine(
                dashboardUseCase.GetDashboards(),
                budgetUseCase.GetBudgets()
            ) { dashboardsResponse, budgetsResponse ->
                dashboardsResponse to budgetsResponse
            }.collect { (dashboardsResponse, budgetsResponse) ->
                when {
                    dashboardsResponse is Response.Error -> {
                        _state.update { it.copy(error = dashboardsResponse.message, isLoading = false) }
                    }
                    budgetsResponse is Response.Error -> {
                        _state.update { it.copy(error = budgetsResponse.message, isLoading = false) }
                    }
                    dashboardsResponse is Response.Success && budgetsResponse is Response.Success -> {
                        val budget = budgetsResponse.data.find { it.budgetId == budgetId }
                        if (budget == null) {
                            _state.update { it.copy(error = "Budget not found", isLoading = false) }
                        } else {
                            val availableDashboards = dashboardsResponse.data
                            val selectedDashboards = availableDashboards.filter { it.dashboardId in budget.dashboardIds }
                            
                            _state.update { currentState ->
                                if (!isInitialized) {
                                    isInitialized = true
                                    val targetValDigits = (budget.targetValue * 100).roundToLong().toString()
                                    currentState.copy(
                                        name = budget.name,
                                        targetValue = targetValDigits,
                                        selectedCategories = budget.categories,
                                        availableDashboards = availableDashboards,
                                        selectedDashboards = selectedDashboards,
                                        isLoading = false
                                    )
                                } else {
                                    currentState.copy(
                                        availableDashboards = availableDashboards,
                                        selectedDashboards = currentState.selectedDashboards.filter { curr ->
                                            availableDashboards.any { it.dashboardId == curr.dashboardId }
                                        },
                                        isLoading = false
                                    )
                                }
                            }
                        }
                    }
                    else -> {
                        // loading
                    }
                }
            }
        }
    }

    fun onNameChange(newName: String) {
        _state.update { it.copy(name = newName) }
    }

    fun onTargetValueChange(newValue: String) {
        val filtered = newValue.filter { it.isDigit() }
        _state.update { it.copy(targetValue = filtered) }
    }

    fun toggleCategory(categoryDetailedCode: String) {
        _state.update { state ->
            val updated = if (state.selectedCategories.contains(categoryDetailedCode)) {
                state.selectedCategories - categoryDetailedCode
            } else {
                state.selectedCategories + categoryDetailedCode
            }
            state.copy(selectedCategories = updated)
        }
    }

    fun toggleDashboard(dashboard: Dashboard) {
        _state.update { state ->
            val updated = if (state.selectedDashboards.any { it.dashboardId == dashboard.dashboardId }) {
                state.selectedDashboards.filter { it.dashboardId != dashboard.dashboardId }
            } else {
                state.selectedDashboards + dashboard
            }
            state.copy(selectedDashboards = updated)
        }
    }

    fun updateBudget(onSuccess: () -> Unit) {
        val currentState = _state.value
        if (currentState.name.isBlank()) {
            _state.update { it.copy(error = "Budget name cannot be empty") }
            return
        }
        if (currentState.selectedCategories.isEmpty()) {
            _state.update { it.copy(error = "At least one category must be selected") }
            return
        }
        val targetDouble = currentState.targetValue.toDoubleOrNull()?.div(100.0) ?: 0.0
        if (targetDouble <= 0.0) {
            _state.update { it.copy(error = "Target value must be greater than $0.00") }
            return
        }

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val dashboardIds = currentState.selectedDashboards.map { it.dashboardId }
            val response = budgetUseCase.UpdateBudget(
                budgetId = budgetId,
                name = currentState.name,
                categories = currentState.selectedCategories,
                targetValue = targetDouble,
                dashboardIds = dashboardIds
            )
            when (response) {
                is Response.Success -> {
                    _state.update { it.copy(isLoading = false, success = true) }
                    onSuccess()
                }
                is Response.Error -> {
                    _state.update { it.copy(isLoading = false, error = response.message) }
                }
                Response.Loading -> {
                    _state.update { it.copy(isLoading = true) }
                }
            }
        }
    }

    fun deleteBudget(onSuccess: () -> Unit) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            val response = budgetUseCase.DeleteBudget(budgetId)
            when (response) {
                is Response.Success -> {
                    _state.update { it.copy(isLoading = false, isDeleted = true) }
                    onSuccess()
                }
                is Response.Error -> {
                    _state.update { it.copy(isLoading = false, error = response.message) }
                }
                Response.Loading -> {
                    _state.update { it.copy(isLoading = true) }
                }
            }
        }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }
}
